/**
 * Recording Card Component
 * 
 * Part of Native Voice Capture feature (Epic 13)
 * Task 2.10: Web Recording Management UI
 * 
 * Displays a recording in card format with metadata and actions.
 */

import React, { useState } from 'react';
import {
  RecordingCardProps,
  formatDuration,
  formatFileSize,
  formatDate,
  getStatusColor,
  getStatusLabel,
  getSourceTypeLabel,
} from './types';
import styles from './RecordingCard.module.css';

export const RecordingCard: React.FC<RecordingCardProps> = ({
  recording,
  onSelect,
  onDelete,
  onDownload,
  selected = false,
  viewMode = 'grid',
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (onDelete) onDelete();
    setShowDeleteConfirm(false);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const statusColor = getStatusColor(recording.status);
  const statusLabel = getStatusLabel(recording.status);
  const sourceLabel = getSourceTypeLabel(recording.sourceType);

  return (
    <div
      className={`${styles.card} ${styles[viewMode]} ${selected ? styles.selected : ''}`}
      onClick={onSelect}
      role="article"
      aria-label={`Recording ${recording.title || recording.id}`}
    >
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h3 className={styles.title}>
            {recording.title || `Recording ${recording.id.slice(0, 8)}`}
          </h3>
          <span className={`${styles.status} ${styles[statusColor]}`}>
            {statusLabel}
          </span>
        </div>
        <span className={styles.source}>{sourceLabel}</span>
      </div>

      {/* Metadata */}
      <div className={styles.metadata}>
        <div className={styles.metadataItem}>
          <span className={styles.metadataLabel}>Duration:</span>
          <span className={styles.metadataValue}>{formatDuration(recording.duration)}</span>
        </div>
        <div className={styles.metadataItem}>
          <span className={styles.metadataLabel}>Size:</span>
          <span className={styles.metadataValue}>{formatFileSize(recording.size)}</span>
        </div>
        <div className={styles.metadataItem}>
          <span className={styles.metadataLabel}>Format:</span>
          <span className={styles.metadataValue}>{recording.format.toUpperCase()}</span>
        </div>
        <div className={styles.metadataItem}>
          <span className={styles.metadataLabel}>Recorded:</span>
          <span className={styles.metadataValue}>{formatDate(recording.recordedAt)}</span>
        </div>
      </div>

      {/* Transcript Preview */}
      {recording.transcriptText && (
        <div className={styles.transcriptPreview}>
          <p className={styles.transcriptText}>
            {recording.transcriptText.length > 150
              ? `${recording.transcriptText.slice(0, 150)}...`
              : recording.transcriptText}
          </p>
        </div>
      )}

      {/* Actions */}
      <div className={styles.actions} onClick={(e) => e.stopPropagation()}>
        {recording.status === 'transcribed' && (
          <>
            {recording.audioUrl && onDownload && (
              <button
                onClick={() => onDownload('audio')}
                className={styles.actionButton}
                aria-label="Download audio"
                title="Download audio file"
              >
                🎵 Audio
              </button>
            )}
            {recording.transcriptUrl && onDownload && (
              <button
                onClick={() => onDownload('transcript')}
                className={styles.actionButton}
                aria-label="Download transcript"
                title="Download transcript"
              >
                📄 Transcript
              </button>
            )}
          </>
        )}

        {onDelete && !showDeleteConfirm && (
          <button
            onClick={handleDelete}
            className={`${styles.actionButton} ${styles.deleteButton}`}
            aria-label="Delete recording"
            title="Delete this recording"
          >
            🗑️ Delete
          </button>
        )}

        {showDeleteConfirm && (
          <div className={styles.deleteConfirm}>
            <span className={styles.confirmText}>Delete?</span>
            <button
              onClick={confirmDelete}
              className={`${styles.confirmButton} ${styles.confirmYes}`}
              aria-label="Confirm delete"
            >
              Yes
            </button>
            <button
              onClick={cancelDelete}
              className={`${styles.confirmButton} ${styles.confirmNo}`}
              aria-label="Cancel delete"
            >
              No
            </button>
          </div>
        )}
      </div>

      {/* Selection Indicator */}
      {selected && <div className={styles.selectedIndicator} aria-hidden="true">✓</div>}
    </div>
  );
};

